import {memo, Suspense, useCallback, useMemo, useState} from 'react'

import {useQueryParamState} from '../../../../../../hooks/routes'
import {usePermission} from '../../../../../../hooks/usePermission'
import type {TestOccurrenceId} from '../../../../../../types'
import {Permission, stringifyId, toTestOccurrenceId} from '../../../../../../types'
import Grid from '../../../../../common/Grid/Grid'
import RowGroup from '../../../../../common/Grid/RowGroup'
import InvestigationMuteButtons from '../../../../../packages/Tests/InvestigationMuteButtons/InvestigationMuteButtons'
import {makeGetTestOccurrencesList} from '../../../../../packages/Tests/Tests.selectors'

import DownloadTestsButton from './DownloadTestsButton/DownloadTestsButton.container'
import TableHeader from './TableHeader/TableHeader.container'
import BuildTestItem from './TestItem/TestItem'
import type {Props} from './TestsList.types'

import styles from './TestsList.css'

import {useAppSelector} from 'src/hooks/react-redux'

function TestsList(props: Props) {
  const {className, ids, buildId, projectId, fetchTestOccurrencesHandler, invocationsArg} = props

  const getTestOccurrencesList = useMemo(makeGetTestOccurrencesList, [])
  const testOccurrences = useAppSelector(state => getTestOccurrencesList(state, ids))

  const hasPermissionForInvestigation = usePermission(Permission.ASSIGN_INVESTIGATION, projectId)
  const hasPermissionForMute = usePermission(Permission.MANAGE_BUILD_PROBLEMS, projectId)

  const [expandedTest, setExpandedTest] = useQueryParamState('expandedTest')
  const [selectedTests, setSelectedTests] = useState<TestOccurrenceId[]>([])

  const toggleExpandedTest = useCallback(
    (id?: TestOccurrenceId | null | undefined) => {
      const idString = stringifyId(id)
      setExpandedTest(expandedTest === idString ? null : idString)
    },
    [expandedTest, setExpandedTest],
  )
  const toggleSelectedTests = useCallback(
    (id?: TestOccurrenceId) => {
      if (id != null) {
        setSelectedTests(
          !selectedTests.includes(id)
            ? [...selectedTests, id]
            : selectedTests.filter(el => el !== id),
        )
      }
    },
    [selectedTests],
  )
  const toggleSelectAllTests = useCallback(() => {
    const tests: TestOccurrenceId[] = []

    if (selectedTests.length !== testOccurrences.length) {
      testOccurrences.forEach(item => item.id != null && tests.push(item.id))
    }

    setSelectedTests(tests)
  }, [selectedTests.length, testOccurrences])

  const onInvestigationMuteAction = useCallback(() => {
    fetchTestOccurrencesHandler()
    setSelectedTests([])
  }, [fetchTestOccurrencesHandler])

  const hasTestOccurrences = testOccurrences.length > 0
  return (
    <div className={className}>
      <div data-test-tests-list-buttons className={styles.actions}>
        <Suspense>
          <InvestigationMuteButtons
            className={styles.investigationMuteButtons}
            projectId={projectId}
            testOccurrencesIds={selectedTests}
            onAction={onInvestigationMuteAction}
          />
        </Suspense>
        {buildId != null ? <DownloadTestsButton buildId={buildId} /> : null}
      </div>
      <Grid className={styles.grid}>
        <>
          {hasTestOccurrences && (
            <RowGroup span={1}>
              <TableHeader
                hasPermissionForMute={hasPermissionForMute}
                hasPermissionForInvestigation={hasPermissionForInvestigation}
                testsCount={testOccurrences.length}
                selectedTestsCount={selectedTests.length}
                onToggleSelectAllTests={toggleSelectAllTests}
              />
            </RowGroup>
          )}
        </>
        {hasTestOccurrences && (
          <div className={styles.testsList} data-test-build-tests-list>
            {testOccurrences.map(testOccurrence => {
              const itemBuildId = buildId ?? testOccurrence.build?.id
              return itemBuildId != null ? (
                <BuildTestItem
                  key={stringifyId(testOccurrence.id)}
                  testOccurrenceId={testOccurrence.id}
                  buildId={itemBuildId}
                  selectedTests={selectedTests}
                  expandedTest={expandedTest != null ? toTestOccurrenceId(expandedTest) : null}
                  toggleExpandedTest={toggleExpandedTest}
                  toggleSelectedTests={toggleSelectedTests}
                  withCheckbox={hasPermissionForInvestigation || hasPermissionForMute}
                  invocationsArg={invocationsArg}
                />
              ) : null
            })}
          </div>
        )}
      </Grid>
    </div>
  )
}

export default memo<Props>(function TestsListContainer(props) {
  return (
    <Suspense>
      <TestsList {...props} />
    </Suspense>
  )
})
